<template>
  <div id="discountsPage">
    <CCard>
      <CCardHeader>
        <CRow class="justify-content-between px-3">
          <div class="d-flex align-items-center">
            Discount:
            <b class="ml-1">{{ totalDiscount | currency100 }}</b>
          </div>
          <div class="d-flex">
            <download-csv
              class="btn btn-default"
              :data="csvDatas"
              :labels="{
                status: 'Status',
                booking_id: 'Booking ID',
                job_id: 'Job ID',
                discount_type: 'Discount type',
                datetime: 'Date and time',
                amount: 'Amount',
              }"
              :fields="[
                'status',
                'booking_id',
                'job_id',
                'discount_type',
                'datetime',
                'amount',
              ]"
              :name="`Comenow_Finance_Discounts.csv`"
            >
              <CIcon name="cil-cloud-download" />
              Download
            </download-csv>
            <CInput
              style="width: 400px"
              class="my-0 px-0"
              :placeholder="`Search BookingID`"
              :value.sync="search.value"
            >
              <template #append>
                <CButton color="primary" @click="doApplyFilter">Search</CButton>
                <CButton
                  class="ml-2"
                  size="sm"
                  color="info"
                  variant="outline"
                  @click="isShowFilter = !isShowFilter"
                >
                  <CIcon :name="isShowFilter ? 'cil-x' : 'cil-arrow-bottom'" />
                  Filter</CButton
                >
              </template>
            </CInput>
          </div>
        </CRow>
      </CCardHeader>
      <CCardBody>
        <CCollapse :show="isShowFilter">
          <CCard body-wrapper>
            <CRow>
              <h5 class="ml-3 flex-grow-1">Filter</h5>
              <CButton
                class="ml-auto m-2"
                variant="outline"
                color="info"
                @click="doResetFilter"
                >Reset</CButton
              >
              <CButton class="m-2" color="info" @click="doApplyFilter"
                >Apply</CButton
              >
            </CRow>
            <CRow>
              <CCol>
                <SelectAllTreeselect
                  v-model="filter.status"
                  :options="filterData.status"
                  placeholder="Select status"
                />
              </CCol>
              <CCol>
                <SelectAllTreeselect
                  v-model="filter.discountType"
                  :options="filterData.discountType"
                  placeholder="Select discount type"
                />
              </CCol>
              <CCol>
                <date-picker
                  v-model="filter.dateRangeSelected"
                  range
                  format="YYYY-MM-DD"
                  placeholder="Created date"
                ></date-picker>
              </CCol>
            </CRow>
          </CCard>
        </CCollapse>
        <CDataTable
          hover
          striped
          border
          sorter
          :items="discounts"
          :fields="fields"
          :loading="loading"
          :items-per-page="25"
          :pagination="{ doubleArrows: false, align: 'center' }"
        >
          <td slot="booking_id" slot-scope="{ item }">
            <span v-if="item.booking_id">
              <CLink :href="'#/bookings/' + item.booking_id">
                {{ item.booking_no || item.booking_id }}
              </CLink>
            </span>
            <span v-else>-</span>
          </td>
          <td slot="job_id" slot-scope="{ item }">
            {{ item.job_id || '-' }}
          </td>
          <td slot="discount_type" slot-scope="{ item }">
            {{ formatDiscountType(item.discount_type) }}
          </td>
          <td slot="created_at" slot-scope="{ item }">
            {{ item.created_at | moment(datetimeFormat) }}
          </td>
          <td slot="amount" slot-scope="{ item }">
            {{ item.amount | currency100 }}
          </td>
        </CDataTable>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { Common } from '@/constants';
import AdminProxy from '@/proxies/admin.proxy';
import moment from 'moment';
import { FormatCurrency100 } from '@/utils/utils';
import SelectAllTreeselect from '@/components/SelectAllTreeselect.vue';

export default {
  name: 'Discounts',
  components: { SelectAllTreeselect },
  data() {
    return {
      datetimeFormat: Common.datetimeFormat,
      loading: false,
      isShowFilter: false,
      fields: [
        { key: 'status', label: 'Status' },
        { key: 'booking_id', label: 'Booking ID' },
        { key: 'job_id', label: 'Job ID' },
        { key: 'discount_type', label: 'Discount type' },
        { key: 'created_at', label: 'Date and time' },
        { key: 'amount', label: 'Amount' },
      ],
      discounts: [],
      csvDatas: [],
      totalDiscount: 0,
      search: {
        value: '',
      },
      filter: {
        status: null,
        discountType: null,
        dateRangeSelected: null,
      },
      filterData: {
        status: [
          { id: 'completed', label: 'Completed' },
          { id: 'cancelled', label: 'Cancelled' },
          { id: 'ongoing', label: 'Ongoing' },
          { id: 'reassigned', label: 'Reassigned' },
        ],
        discountType: [
          { id: 'credits', label: 'Credits' },
          { id: 'referral', label: 'Referral' },
          { id: 'reversal', label: 'Reversal' },
        ],
      },
    };
  },
  mounted() {
    this.doApplyFilter();
  },
  methods: {
    formatDiscountType(type) {
      if (!type) {
        return '-';
      }

      const knownLabels = {
        credits: 'Credits',
        referral: 'Referral',
        reversal: 'Reversal',
      };
      return type
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => knownLabels[value.toLowerCase()] || value)
        .join(', ');
    },
    syncDiscountTypeOptions(discounts) {
      const known = new Set(
        this.filterData.discountType.map((option) => option.id)
      );
      discounts.forEach((item) => {
        String(item.discount_type || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
          .forEach((value) => {
            const normalized = value.toLowerCase();
            if (
              ['credits', 'referral', 'reversal'].includes(normalized) ||
              known.has(value)
            ) {
              return;
            }
            this.filterData.discountType.push({ id: value, label: value });
            known.add(value);
          });
      });
    },
    doApplyFilter() {
      const query = {};

      if (this.search.value?.length > 0) {
        query.booking_no = this.search.value;
      }

      if (this.filter.status?.length > 0) {
        query.status = this.filter.status;
      }

      if (this.filter.discountType?.length > 0) {
        query.discount_type = this.filter.discountType;
      }

      if (
        this.filter.dateRangeSelected?.length > 0 &&
        this.filter.dateRangeSelected[0] &&
        this.filter.dateRangeSelected[1]
      ) {
        query.from = moment(this.filter.dateRangeSelected[0])
          .startOf('day')
          .format('YYYY-MM-DD');
        query.to = moment(this.filter.dateRangeSelected[1])
          .endOf('day')
          .format('YYYY-MM-DD');
      }

      this.loading = true;
      AdminProxy.getFinanceDiscounts(query)
        .then(({ data }) => {
          this.discounts = data || [];
          this.syncDiscountTypeOptions(this.discounts);
          this.processCSVData();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    doResetFilter() {
      this.filter.status = null;
      this.filter.discountType = null;
      this.filter.dateRangeSelected = null;
      this.search.value = '';
      this.doApplyFilter();
    },
    processCSVData() {
      this.csvDatas = [];
      let totalDiscount = 0;

      this.discounts.forEach((item) => {
        totalDiscount += item.amount;
        this.csvDatas.push({
          status: item.status || '-',
          booking_id: item.booking_no || item.booking_id || '-',
          job_id: item.job_id || '-',
          discount_type: this.formatDiscountType(item.discount_type),
          datetime: moment(item.created_at).format('YYYY-MM-DD HH:mm:ss'),
          amount: FormatCurrency100(item.amount),
        });
      });

      this.totalDiscount = totalDiscount;

      this.csvDatas.push({
        status: 'Total',
        amount: FormatCurrency100(totalDiscount),
      });
    },
  },
};
</script>

<style></style>
